import { DEADLINE_STATUS } from './constant';

export const PROPOSAL_DEADLINE_DAYS = 7;
export const PROPOSAL_DEADLINE_MS =
  PROPOSAL_DEADLINE_DAYS * 24 * 60 * 60 * 1000;

/**
 * Calculate the time remaining from a creation date with a 7-day deadline
 * @param {string} createdAt - The creation date string (ISO format)
 * @returns {string} - Formatted remaining time (e.g., '6 days 23 hours 59 minutes 59 seconds')
 */
export const calculateTimeRemaining = (createdAt) => {
  if (!createdAt) return '0 days 0 hours 0 minutes 0 seconds';

  const createdDate = new Date(createdAt);
  const deadlineDate = new Date(createdDate.getTime() + PROPOSAL_DEADLINE_MS);
  const now = new Date();

  const diff = deadlineDate - now;

  if (diff <= 0) {
    return DEADLINE_STATUS.EXPIRED;
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return `${days} days ${hours} hours ${minutes} minutes ${seconds} seconds`;
};

/**
 * Calculate a short formatted remaining time from a creation date with a 7-day deadline
 * @param {string} createdAt - The creation date string (ISO format)
 * @returns {string} - Short formatted remaining time (e.g., '2 days left', '5 hours left')
 */
export const calculateShortTimeRemaining = (createdAt) => {
  if (!createdAt) return '';

  const createdDate = new Date(createdAt);
  const deadlineDate = new Date(createdDate.getTime() + PROPOSAL_DEADLINE_MS);
  const now = new Date();

  const diff = deadlineDate - now;

  if (diff <= 0) {
    return DEADLINE_STATUS.EXPIRED;
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days > 0) return `${days} day${days !== 1 ? 's' : ''} left`;

  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours > 0) return `${hours} hour${hours !== 1 ? 's' : ''} left`;

  const minutes = Math.floor(diff / (1000 * 60));
  return `${minutes} minute${minutes !== 1 ? 's' : ''} left`;
};

/**
 * Calculate time ago from a given date
 * @param {string} dateString - The date string
 * @returns {string} - Formatted time ago string (e.g., '2 hours ago')
 */
export const calculateTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;

  return formatDate(dateString);
};

/**
 * Normalize a date string to ISO format (YYYY-MM-DD)
 * Handles "DD Mon YYYY" format
 * @param {string} value - The date string to normalize
 * @returns {string} - ISO formatted date string
 */
export const normalizeToISODate = (value) => {
  if (typeof value !== 'string' || !value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const monthMap = {
    Jan: '01',
    Feb: '02',
    Mar: '03',
    Apr: '04',
    May: '05',
    Jun: '06',
    Jul: '07',
    Aug: '08',
    Sep: '09',
    Oct: '10',
    Nov: '11',
    Dec: '12',
  };
  const parts = value.split(' ');
  if (parts.length === 3) {
    const [day, monthAbbr, year] = parts;
    const month = monthMap[monthAbbr];
    if (month) {
      return `${year}-${month}-${day.padStart(2, '0')}`;
    }
  }
  return '';
};

/**
 * Format a date string to 'DD MMM YYYY' format
 * @param {string} value - The date string (ISO format preferred)
 * @returns {string} - Formatted date string (e.g., '30 Dec 2025')
 */
export const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};
